// src/components/LinkedIn/LinkedInFeed.js
// Cookie-free LinkedIn company feed component
// Displays recent posts from ROBOMIND Systems LinkedIn company page

import React from 'react';
import { useTranslation } from 'react-i18next';
import LinkedInPost from './LinkedInPost';

const LinkedInFeed = ({ maxPosts = 3 }) => {
    const { t } = useTranslation();
    const linkedInCompanyUrl = 'https://www.linkedin.com/company/robomindsystems';

    // Recent LinkedIn posts from ROBOMIND Systems
    // In a production environment, you could fetch these via LinkedIn API
    // For now, we'll use static data that matches your actual posts
    const recentPosts = [
        {
            id: 'data-driven-manufacturing',
            title: t('linkedin.posts.data_driven_manufacturing.title'),
            excerpt: t('linkedin.posts.data_driven_manufacturing.excerpt'),
            url: 'https://www.linkedin.com/company/robomindsystems/posts/',
            date: '2026-07-11',
            image: null // No image - will not show preview
        },
        {
            id: 'industry-days-team',
            title: t('linkedin.posts.industry_days_team.title'),
            excerpt: t('linkedin.posts.industry_days_team.excerpt'),
            url: 'https://www.linkedin.com/company/robomindsystems/posts/',
            date: '2026-05-21',
            image: null // No image - will not show preview
        },
        {
            id: 'industry-days-expo',
            title: t('linkedin.posts.industry_days_expo.title'),
            excerpt: t('linkedin.posts.industry_days_expo.excerpt'),
            url: 'https://www.linkedin.com/company/robomindsystems/posts/',
            date: '2026-05-18',
            image: null // No image - will not show preview
        }
    ];

    return (
        <div className="bg-gray-50 py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                        {t('linkedin.feed.title')}
                    </h2>
                    <p className="mt-4 text-xl text-gray-600">
                        {t('linkedin.feed.subtitle')}
                    </p>
                    <a 
                        href={linkedInCompanyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center text-primary hover:text-primary-hover font-semibold"
                    >
                        {t('linkedin.feed.follow_us')}
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                    </a>
                </div>

                {/* Posts Grid */}
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {recentPosts.slice(0, maxPosts).map((post) => (
                        <LinkedInPost
                            key={post.id}
                            url={post.url}
                            title={post.title}
                            excerpt={post.excerpt}
                            showPreview={!!post.image}
                            previewImage={post.image}
                        />
                    ))}
                </div>

                {/* Follow Button */}
                <div className="mt-8 text-center">
                    <a
                        href={linkedInCompanyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                    >
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                        {t('linkedin.feed.follow_on_linkedin')}
                    </a>
                </div>
            </div>
        </div>
    );
};

export default LinkedInFeed;

